import { useState, type FormEvent } from 'react';
import { Search as SearchIcon, Loader2, Folder } from 'lucide-react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { SlimSidebar } from '../layout/SlimSidebar';
import { useSearch, type SearchHit } from '../../hooks/useSearch';
import { HelpHint } from '../ui/HelpHint';

/**
 * Vista Ricerca storico.
 * Cerca su TUTTA la posta Merak (tutte le cartelle della casella).
 * I risultati non hanno id DB → solo anteprima, nessuna apertura dettaglio.
 */
export function SearchView() {
  // `input` è il testo digitato, `query` è ciò che effettivamente si cerca (on submit)
  const [input, setInput] = useState('');
  const [query, setQuery] = useState('');

  const { data, isFetching, isError } = useSearch(query, 100);

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setQuery(input.trim());
  }

  const hasSearched = query.trim().length >= 2;

  return (
    <div className="flex flex-1 overflow-hidden">
      <SlimSidebar />

      <section className="flex flex-1 flex-col bg-zinc-950">
        {/* Barra ricerca */}
        <div className="border-b border-zinc-800/80 px-6 py-4">
          {/* Intestazione minimale con aiuto contestuale della sezione Ricerca */}
          <div className="mx-auto mb-3 flex max-w-2xl items-center gap-2">
            <span className="text-sm font-semibold" style={{ color: 'var(--text)' }}>
              Ricerca
            </span>
            <HelpHint topic="search" />
          </div>
          <form onSubmit={onSubmit} className="mx-auto max-w-2xl">
            <div className="group flex items-center rounded-lg border border-zinc-800 bg-zinc-900/60 px-3 py-2 transition focus-within:border-accent-500/50">
              <SearchIcon size={16} className="text-zinc-500" />
              <input
                autoFocus
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Cerca in tutta la casella…"
                className="flex-1 bg-transparent px-3 text-sm placeholder-zinc-500 outline-none"
              />
              <button
                type="submit"
                className="rounded-md bg-accent-500 px-3 py-1 text-xs font-medium text-white transition hover:bg-accent-600"
              >
                Cerca
              </button>
            </div>
            <p className="mt-2 text-center text-[11px] text-zinc-500">
              Ricerca su tutte le cartelle della casella
            </p>
          </form>
        </div>

        {/* Risultati */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="mx-auto max-w-2xl">
            {!hasSearched ? (
              <EmptyState
                title="Cerca nel tuo storico"
                description="Digita almeno 2 caratteri e premi Cerca per interrogare tutte le cartelle della casella Merak."
              />
            ) : isFetching ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 size={16} className="animate-spin text-zinc-600" />
              </div>
            ) : isError ? (
              <EmptyState
                title="Errore nella ricerca"
                description="Non è stato possibile completare la ricerca. Riprova tra qualche istante."
              />
            ) : !data || data.hits.length === 0 ? (
              <EmptyState
                title="Nessun risultato"
                description={`Nessun messaggio trovato per "${query}".`}
              />
            ) : (
              <>
                <div className="mb-3 flex items-center justify-between text-xs text-zinc-500">
                  <span>{data.hits.length} risultati</span>
                  {data.truncated && (
                    <span className="rounded bg-amber-500/15 px-2 py-0.5 text-[10px] font-medium text-amber-300">
                      Risultati troncati · affina la ricerca
                    </span>
                  )}
                </div>
                <ul className="space-y-1.5">
                  {data.hits.map((h) => (
                    <SearchRow key={`${h.folder}:${h.uid}`} hit={h} />
                  ))}
                </ul>
              </>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function SearchRow({ hit }: { hit: SearchHit }) {
  const sender = hit.from_name ?? hit.from_email ?? '(sconosciuto)';
  return (
    <li className="rounded-lg border border-zinc-800/80 bg-zinc-900/30 p-3">
      <div className="flex items-center justify-between gap-2">
        <span className="truncate text-sm font-medium text-zinc-200">{sender}</span>
        <span className="shrink-0 text-[10px] text-zinc-500">
          {hit.date ? format(new Date(hit.date), 'dd MMM yyyy', { locale: it }) : ''}
        </span>
      </div>
      <div className="mt-0.5 truncate text-xs text-zinc-400">
        {hit.subject ?? '(senza oggetto)'}
      </div>
      {hit.snippet && (
        <p className="mt-1.5 line-clamp-2 text-[12px] leading-relaxed text-zinc-500">
          {hit.snippet}
        </p>
      )}
      {/* Badge cartella di provenienza */}
      <div className="mt-2 flex items-center gap-1.5">
        <span className="inline-flex items-center gap-1 rounded bg-zinc-800/80 px-1.5 py-0.5 text-[9px] font-medium text-zinc-400">
          <Folder size={9} />
          {hit.folder}
        </span>
      </div>
    </li>
  );
}

function EmptyState({ title, description }: { title: string; description: string }) {
  return (
    <div className="flex flex-col items-center justify-center px-8 py-16 text-center">
      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-zinc-900 ring-1 ring-zinc-800">
        <SearchIcon size={20} className="text-zinc-600" />
      </div>
      <h3 className="text-sm font-medium text-zinc-300">{title}</h3>
      <p className="mt-1 max-w-xs text-xs text-zinc-500">{description}</p>
    </div>
  );
}
